import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Phone, Envelope, MapPin, Clock } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { COMPANY, SERVICES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-line bg-coal px-5 py-4 text-sm text-bone placeholder:text-ash/60 outline-none transition-colors duration-300 focus:border-ember";

const InfoRow = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-5 border-b border-line py-6">
    <Icon size={20} className="mt-0.5 shrink-0 text-ember" />
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ash">{label}</p>
      <div className="mt-2 text-base text-bone" data-testid={testId}>{children}</div>
    </div>
  </div>
);

export default function Kontakt() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service: form.service || null,
        message: form.message,
      });
      toast.success("Vielen Dank! Ihre Anfrage ist bei uns eingegangen — wir melden uns zeitnah.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns direkt an.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="kontakt-page">
      <section className="border-b border-line">
        <div className="mx-auto max-w-[1600px] px-6 pb-20 pt-40 md:px-12 md:pt-52">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Kontakt</p>
            <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter text-bone md:text-8xl">
              Sprechen wir<br />über Ihr Projekt
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1600px] gap-16 px-6 py-24 md:px-12 lg:grid-cols-5">
        <Reveal className="lg:col-span-2">
          <p className="text-base leading-relaxed text-ash">
            Ob Neubau, Sanierung oder Außenanlage — schreiben Sie uns oder rufen Sie direkt an. Wir antworten persönlich und zeitnah.
          </p>
          <div className="mt-12" data-testid="contact-info">
            <InfoRow icon={MapPin} label="Adresse" testId="contact-address">
              {COMPANY.name}<br />
              Inh. {COMPANY.owner}<br />
              {COMPANY.street}<br />
              {COMPANY.city}
            </InfoRow>
            <InfoRow icon={Phone} label="Telefon" testId="contact-phone">
              <a href={COMPANY.phoneHref} className="transition-colors duration-300 hover:text-ember">{COMPANY.phone}</a>
            </InfoRow>
            <InfoRow icon={Envelope} label="E-Mail" testId="contact-email">
              <a href={COMPANY.emailHref} className="break-all transition-colors duration-300 hover:text-ember">{COMPANY.email}</a>
            </InfoRow>
            <InfoRow icon={Clock} label="Erreichbarkeit" testId="contact-hours">
              Mo–Fr nach Vereinbarung<br />
              <span className="text-sm text-ash">Termine auf der Baustelle jederzeit möglich</span>
            </InfoRow>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-3">
          <form onSubmit={submit} className="border border-line bg-ink p-8 md:p-12" data-testid="contact-form">
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-ash">Name *</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Ihr Name" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-ash">E-Mail *</label>
                <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="ihre@email.de" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-phone" className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-ash">Telefon</label>
                <input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="Optional" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-service" className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-ash">Gewerk</label>
                <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                  <option value="">Bitte wählen</option>
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.title}>{s.title}</option>
                  ))}
                  <option value="Sonstiges">Sonstiges</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="contact-message" className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-ash">Ihre Nachricht *</label>
                <textarea id="contact-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} placeholder="Beschreiben Sie kurz Ihr Bauvorhaben …" className={`${inputCls} resize-none`} />
              </div>
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="group mt-10 inline-flex items-center gap-3 bg-ember px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-bone disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? "Wird gesendet …" : "Anfrage senden"}
              <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <p className="mt-6 text-xs leading-relaxed text-ash">
              Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten gemäß unserer Datenschutzerklärung zu.
            </p>
          </form>
        </Reveal>
      </section>
    </main>
  );
}
